import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import LoadingSpinner from "../components/LoadingSpinner";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { apiService } from "../services/api";

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // useGoogleLogin opens a standard OAuth2 popup — no FedCM, no script injection,
  // no StrictMode double-init issues.
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      try {
        // Fetch user info using the access token (same as Android iOS flow)
        const userInfoRes = await fetch(
          "https://www.googleapis.com/userinfo/v2/me",
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );
        const userInfo = await userInfoRes.json();

        // Update profile on backend — no PII logged
        await apiService.put("/auth/profile/", {
          email: userInfo.email,
          google_signin_completed: true,
        });

        // Patch local state directly (matches Android updateUser behaviour).
        // refreshUser() would re-fetch from backend which doesn't persist this
        // field, keeping google_signin_completed=false and bouncing the route.
        updateUser({ email: userInfo.email, google_signin_completed: true });
        toast.success("Email verified successfully!");
        navigate("/pan-details");
      } catch {
        toast.error("Failed to verify email. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      toast.error("Google sign-in failed. Please try again.");
    },
  });

  // Back navigates without logging out (matches Android onBack behaviour)
  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center px-6 py-4">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          disabled={isLoading}
        >
          <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center px-6">
        <div className="max-w-sm mx-auto w-full text-center">
          {/* Title */}
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">
            Verify your email
          </h1>

          {/* Subtitle — matches Android */}
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">
            We'll use this to send important updates
          </p>

          {/* Google Sign-In Button */}
          <button
            onClick={() => googleLogin()}
            disabled={isLoading}
            className="w-full bg-white border border-primary-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-primary-600 font-medium py-4 px-6 rounded-xl transition-colors flex items-center justify-center text-base gap-3"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" />
                Signing in...
              </>
            ) : (
              <>
                {/* Google logo */}
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
